import { cn } from "~/lib/utils";
import React, { Fragment } from "react";
import {
  DataTableColumnHeaderProps,
  justifyToClass,
} from "./data-table-column-header";
import { Icon } from "../icon";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableProps } from "./data-table";
import { Cell, flexRender, Row } from "@tanstack/react-table";
import { Skeleton } from "../skeleton";

export const CardDataTable = <TData, TValue>({
  table,
  variant = "default",
  onRowClick,
  onCellClick,
  hideFooter,
  rowsPerPageOptions,
  name,
  getBorderClass,
  size = "md",
}: DataTableProps<TData, TValue> & {
  getBorderClass?: (row: Row<TData>) => string;
}) => {
  const isClickable = variant === "clickable";
  const isShowingAllRows = (table.options.meta as { showAllRows?: boolean })
    ?.showAllRows;
  const shouldHideFooter = isShowingAllRows || hideFooter;

  return (
    <div className="flex flex-col w-full gap-8">
      <div className="overflow-scroll">
        <CardTable
          className={cn(
            "border-spacing-0 [&>thead>tr>th]:px-2",
            variant === "clickable" && "[&_tr]:cursor-pointer",
            hideFooter && "pb-8",
            {
              "text-small": size === "sm",
            }
          )}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) =>
                  header.isPlaceholder ? null : (
                    <Fragment key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Fragment>
                  )
                )}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const borderClass = getBorderClass?.(row) ?? "";

              return (
                <Fragment key={row.id}>
                  {/* Spacer row */}
                  <tr className="h-2" />

                  {/* Normal row UI */}
                  <tr
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      "group relative border-b-4 select-none",
                      isClickable && "cursor-pointer"
                    )}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      const isActions = cell.column.id === "actions";
                      const isDocs = cell.column.id === "docs";

                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            "h-14 border-y-2 px-2",
                            borderClass,
                            {
                              "rounded-l-xl border-l-2 border-y-2 border-r-0":
                                !index,
                              "rounded-r-xl border-r-2 border-y-2 border-l-0":
                                index === row.getVisibleCells().length - 1,
                            },
                            cell.column.getIsSorted() && "font-semibold",
                            isActions && "w-32",
                            isDocs && "w-24"
                          )}
                          onClick={
                            onCellClick
                              ? () => onCellClick(cell as Cell<TData, TValue>)
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Spacer row */}
                  <tr className="h-2" />
                </Fragment>
              );
            })}
          </tbody>
        </CardTable>
      </div>

      {!shouldHideFooter && (
        <DataTablePagination
          table={table}
          rowsPerPageOptions={rowsPerPageOptions}
          name={name}
        />
      )}
    </div>
  );
};

export const CardTable = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => {
  return (
    <table
      ref={ref}
      className={cn(
        "text-sm w-full border-separate border-spacing-y-4",
        className
      )}
      {...props}
    />
  );
});

CardTable.displayName = "CardTable";

export const CardDataTableColumnHeader = <TData, TValue>({
  column,
  title,
  className,
  justify = "left",
  children,
}: DataTableColumnHeaderProps<TData, TValue>) => {
  if (!column.getCanSort()) {
    return (
      <CardTableHead className={cn("cursor-default", className)}>
        {children || title}
      </CardTableHead>
    );
  }

  const sortOrder = column.getIsSorted();

  const handleSorting = () => {
    column.toggleSorting();
  };

  return (
    <CardTableHead
      onClick={handleSorting}
      className={cn(
        "group cursor-pointer",
        column.getIsSorted() && "text-foreground",
        className
      )}
    >
      <span
        className={cn(
          "relative flex items-center gap-1 w-fit",
          justifyToClass[justify]
        )}
      >
        {children || <span className="truncate">{title}</span>}
        <span
          className={cn(
            "absolute -right-3 flex items-center text-card-foreground opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:animate-in group-hover:zoom-in-110",
            {
              "opacity-100 animate-in zoom-in-110": sortOrder,
              "rotate-180": sortOrder === "asc",
              "right-2": children,
            }
          )}
        >
          <Icon name="arrow-down" />
        </span>
      </span>
    </CardTableHead>
  );
};

export const CardTableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  return (
    <th
      ref={ref}
      className={cn(
        "text-xs text-muted-foreground font-semibold select-none uppercase px-4",
        className
      )}
      {...props}
    />
  );
});

CardTableHead.displayName = "CardTableHead";

export const CardDataTableSkeleton = ({
  rows = 10,
  className,
}: {
  rows?: number;
  className?: string;
}) => {
  return (
    <div className={cn("space-y-4", className)}>
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className="h-14 w-full rounded-lg opacity-80" />
      ))}
    </div>
  );
};
